/**
 * The board's project filter: "All", then each active project with its open
 * tasks, as one choice. Hidden with a single active project (a pin window
 * holds only its own). Cards learn through `BoardProjectsContext` whether the
 * board shows more than one project, and then name their project.
 */
import { useMemo } from "react";
import { ChipGroup } from "@/components/ui";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { framesTaskboardMessages } from "@/i18n/messages/frames-taskboard";
import { useProjectStore } from "@/stores/project-store";
import type { BoardProjects } from "@/stores/task-view-session";
import { useTasksStore } from "@/stores/tasks-store";
import { useBoardTasks } from "./task-display-filters";

export function BoardProjectChips({
  value,
  onChange,
  className,
}: {
  value: BoardProjects;
  onChange: (projects: BoardProjects) => void;
  className?: string;
}) {
  const t = useT(framesTaskboardMessages);
  const projects = useProjectStore((state) => state.projects);
  const tasks = useBoardTasks("all");
  const truncated = useTasksStore((state) => state.truncated);
  const openCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const task of tasks)
      if (task.status !== "done")
        counts.set(task.project, (counts.get(task.project) ?? 0) + 1);
    return counts;
  }, [tasks]);
  const filters = projects.length > 1;
  if (!filters && !truncated) return null;
  const openTotal = [...openCounts.values()].reduce((a, b) => a + b, 0);
  return (
    <div
      className={cn("nodrag flex shrink-0 flex-col gap-1", className)}
      data-testid="board-project-filter"
    >
      {filters && (
        <ChipGroup
          value={value}
          onChange={onChange}
          variant="chips"
          ariaLabel={t("projectFilter")}
          options={[
            {
              value: "all",
              label: t("allProjects"),
              count: openTotal,
              testId: "board-project-chip-all",
            },
            ...projects.map((project) => ({
              value: project.id,
              label: project.name,
              count: openCounts.get(project.id) ?? 0,
              testId: `board-project-chip-${project.id}`,
            })),
          ]}
        />
      )}
      {truncated && (
        <p
          className="text-appkit-small text-muted"
          data-testid="board-tasks-truncated"
        >
          {t("openTasksTruncated")}
        </p>
      )}
    </div>
  );
}
